import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Alert,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import OnboardingLayout from '@/components/OnboardingLayout';
import CircularArrowButton from '@/components/CircularArrowButton';
import { Svg, Path } from 'react-native-svg';

export default function FirstAccessScreen() {
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const router = useRouter();

  useEffect(() => {
    // PRD §5.1 — `auth_signup_email_screen_viewed` micro event fires once on
    // mount of the canonical signup entry screen.
    import('@/utils/clarityEvents')
      .then(({ logAuthFirstAccessStarted }) => logAuthFirstAccessStarted())
      .catch(() => {});
  }, []);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleTermsPress = () => {
    router.push({
      pathname: '/webview',
      params: {
        url: 'https://www.padrinho.app/termos',
        title: t('onboarding.firstAccess.termsTitle')
      }
    });
  };

  const handleEmailValidation = async () => {
    if (!email) {
      Alert.alert(t('onboarding.firstAccess.errors.emailRequired'), t('onboarding.firstAccess.errors.emailRequiredMessage'));
      return;
    }

    if (!validateEmail(email)) {
      Alert.alert(t('onboarding.firstAccess.errors.emailInvalid'), t('onboarding.firstAccess.errors.emailInvalidMessage'));
      return;
    }

    if (!termsAccepted) {
      Alert.alert(t('onboarding.firstAccess.errors.termsRequired'), t('onboarding.firstAccess.errors.termsRequiredMessage'));
      return;
    }

    setLoading(true);

    try {
      const normalizedEmail = email.toLowerCase().trim();
      
      console.log('📧 Sending OTP for first access:', normalizedEmail);
      
      const response = await fetch('https://mobile.padrinho.app/api/auth/send-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: normalizedEmail,
          purpose: 'signup'
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        console.error('❌ API error sending OTP:', data);

        // PRD §5.1 — `auth_email_submitted` failure branch. `result` is the
        // bounded enum; `user_already_exists` is the only mapped 409 path.
        import('@/utils/clarityEvents')
          .then(({ logAuthFirstAccessEmailSubmitted }) =>
            logAuthFirstAccessEmailSubmitted({
              result:
                data.code === 'USER_ALREADY_EXISTS' || response.status === 409
                  ? 'existing_user'
                  : 'error',
            })
          )
          .catch(() => {});

        // Handle user already exists error
        if (data.code === 'USER_ALREADY_EXISTS' || response.status === 409) {
          Alert.alert(
            t('onboarding.firstAccess.errors.userExists'),
            t('onboarding.firstAccess.errors.userExistsMessage'),
            [
              { text: t('onboarding.firstAccess.errors.cancel'), style: 'cancel' },
              { 
                text: t('onboarding.firstAccess.errors.goToLogin'), 
                onPress: () => router.replace('/login')
              }
            ]
          );
          return;
        }
        
        Alert.alert(t('onboarding.firstAccess.errors.sendError'), data.error || t('onboarding.firstAccess.errors.sendErrorMessage'));
        return;
      }

      console.log('✅ OTP sent successfully');

      // Fire-and-forget Clarity event. No PII: purpose is an enum, no email.
      import('@/utils/clarityEvents')
        .then(({ logClarityEvent }) =>
          logClarityEvent('auth_otp_requested', { purpose: 'signup', source: 'first_access' })
        )
        .catch(() => {});

      // PRD §5.1 — `auth_email_submitted` success branch. Pairs with the
      // `auth_otp_requested` macro so the email-funnel step has both intent
      // and outcome volume in the dashboard.
      import('@/utils/clarityEvents')
        .then(({ logAuthFirstAccessEmailSubmitted }) =>
          logAuthFirstAccessEmailSubmitted({ result: 'new_user' })
        )
        .catch(() => {});

      router.push({
        pathname: '/verify-code',
        params: { 
          email: normalizedEmail,
          termsAccepted: 'true'
        }
      });

    } catch (error) {
      console.error('❌ Unexpected error during email validation:', error);
      // PRD §5.1 — `auth_email_submitted` failure branch for the unexpected
      // catch (network / JSON parse / etc).
      import('@/utils/clarityEvents')
        .then(({ logAuthFirstAccessEmailSubmitted }) =>
          logAuthFirstAccessEmailSubmitted({ result: 'error' })
        )
        .catch(() => {});
      Alert.alert(t('onboarding.firstAccess.errors.unexpectedError'), t('onboarding.firstAccess.errors.unexpectedErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const progressWidth = 72; // Step 6 of 7

  return (
    <OnboardingLayout progressWidth={progressWidth}>
      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView 
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Step Section */}
          <View style={styles.section}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepNumber}>{t('onboarding.firstAccess.stepNumber')}</Text>
            </View>

            <View style={styles.textContainer}>
              <Text style={styles.title}>{t('onboarding.firstAccess.title')}</Text>
              <Text style={styles.subtitle}>
                {t('onboarding.firstAccess.subtitle')}
              </Text>
            </View>
          </View>

          {/* Email Field Section */}
          <View style={styles.fieldSection}>
            <View style={styles.field}>
              <Text style={styles.label}>{t('onboarding.firstAccess.emailLabel')}</Text>
              <TextInput
                style={styles.input}
                placeholder={t('onboarding.firstAccess.emailPlaceholder')}
                placeholderTextColor="#A19F97"
                color="#151515"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="email"
                returnKeyType="next"
                onSubmitEditing={handleEmailValidation}
              />
            </View>

            {/* Terms Acceptance */}
            <View style={styles.termsContainer}>
              <TouchableOpacity 
                style={styles.checkboxContainer}
                onPress={() => setTermsAccepted(!termsAccepted)}
                activeOpacity={0.7}
              >
                <View style={[styles.checkbox, termsAccepted && styles.checkboxChecked]}>
                  {termsAccepted && (
                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <Path 
                        d="M13.3346 4L6.0013 11.3333L2.66797 8" 
                        stroke="#FFFFFF" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </Svg>
                  )}
                </View>
              </TouchableOpacity>
              <View style={styles.termsTextContainer}>
                <Text style={styles.termsNormalText}>{t('onboarding.firstAccess.termsAccept')}</Text>
                <TouchableOpacity onPress={handleTermsPress} activeOpacity={0.7}>
                  <Text style={styles.termsLinkText}>
                    {t('onboarding.firstAccess.termsLink')}
                  </Text>
                </TouchableOpacity>
                <Text style={styles.termsNormalText}>{t('onboarding.firstAccess.termsAppName')}</Text>
              </View>
            </View>
          </View>
        </ScrollView>

        {/* Continue Button */}
        <View style={[styles.buttonContainer, { bottom: insets.bottom + 24 }]}>
          <CircularArrowButton
            onPress={handleEmailValidation}
            disabled={!termsAccepted || !email.trim()}
            loading={loading}
          />
        </View>
      </KeyboardAvoidingView>
    </OnboardingLayout>
  );
}

const styles = StyleSheet.create({
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    padding: 24,
    paddingBottom: 0,
    alignItems: 'flex-start',
    gap: 24,
  },
  stepBadge: {
    backgroundColor: '#151515',
    borderRadius: 32,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    lineHeight: 28,
  },
  textContainer: {
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    width: 345,
  },
  subtitle: {
    color: '#56554D',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    width: 345,
  },
  fieldSection: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 12,
  },
  field: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 12,
  },
  label: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 14,
  },
  input: {
    width: '100%',
    height: 48,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 20,
    color: '#151515',
  },
  termsContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
    marginTop: 12,
  },
  checkboxContainer: {
    paddingTop: 2,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#015480',
    borderColor: '#015480',
  },
  termsTextContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  termsNormalText: {
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
  },
  termsLinkText: {
    color: '#015480',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 20,
    textDecorationLine: 'underline',
  },
  buttonContainer: {
    position: 'absolute',
    right: 24,
  },
});
